/**
 * Card marks in edit mode (ADR-115 Amendments 3 and 4; handoff §6, §6.2, §16).
 *
 * - `NewCardsSection`, on the «Слова» tab: the words
 *   marked as cards, each with × to unmark it (only the mark goes: nothing here deletes
 *   a card), and the receiving deck.
 * - `CardDeckChooser`: the receiving deck, always the learner's choice while there are
 *   card marks (Amendment 4 Decision 1). The options are the text's decks, a new deck
 *   named after the text (name editable) and any other own deck; the default is the
 *   text's one deck, none with several, a new deck with none. The chosen deck is
 *   linked by the save.
 * - `NewDeckFields`: the new deck's name and its daily-queue switch, as in step 3;
 *   also under «Колоды» when the deck came from «Привязать колоду».
 * - `EditCardsSummary`, in the save sheet: what the server's budget check says the
 *   save would do with the marks: accepted, waiting with the refresh time, already in
 *   the deck. Every count and time is the server's.
 */
import { useState } from 'react'
import { ChevronRight, Clock, X } from 'lucide-react'
import { useLocale } from '../../../i18n/context'
import { useTextEditStore } from '../../../stores/textEditStore'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../../utils/controlSurface'
import { deckChoice, receivingDeck, type EditDraft } from '../../../utils/textEdit'
import { budgetView } from '../../../utils/textFlow'
import type { PlannedCardWord, TextBudgetCheck } from '../../../types'
import DeckPickerSheet from '../DeckPickerSheet'
import { useRefreshWhen } from '../useRefreshWhen'
import { Eyebrow, InfoPanel, Radio, Switch, WordChip } from '../ui'

const muted = { margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--text-secondary)' } as const

function RadioRow({ on, label, desc, chevron, onSelect, testId, first }: {
  on: boolean
  label: string
  desc?: string | null
  chevron?: boolean
  onSelect: () => void
  testId: string
  first: boolean
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      data-testid={testId}
      onClick={onSelect}
      style={{
        width: '100%', display: 'flex', alignItems: 'flex-start', gap: 12, textAlign: 'left', padding: '12px 14px',
        border: 'none', borderTop: first ? 'none' : '1px solid var(--border)', cursor: 'pointer',
        background: on ? 'var(--accent-soft)' : 'transparent', fontFamily: 'var(--font-ui)', color: 'var(--text-primary)',
      }}
    >
      <span style={{ marginTop: 1 }}><Radio on={on} /></span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 15, fontWeight: 600, overflowWrap: 'anywhere' }}>{label}</span>
        {desc && <span style={{ display: 'block', marginTop: 2, ...muted }}>{desc}</span>}
      </span>
      {chevron && <ChevronRight size={18} aria-hidden style={{ marginTop: 2, color: 'var(--text-secondary)' }} />}
    </button>
  )
}

/** The new deck's name (empty: the text's title) and its daily-queue switch. */
export function NewDeckFields({ draft }: { draft: EditDraft }) {
  const { tr } = useLocale()
  const te = tr.textEdit
  const ts = tr.textFlow.save
  const store = useTextEditStore()
  return (
    <div data-testid="new-deck-fields" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{te.newDeckName}</span>
        <input
          type="text"
          value={draft.newDeck?.title ?? ''}
          placeholder={draft.title.trim()}
          maxLength={300}
          onChange={(e) => store.setNewDeck({ title: e.target.value === '' ? null : e.target.value })}
          data-testid="new-deck-name"
          style={{
            height: 44, boxSizing: 'border-box', padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--bg-surface)', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', fontSize: 15,
          }}
        />
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 2px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div id="new-deck-queue" style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{ts.queueSwitch.title}</div>
          <div style={{ marginTop: 2, ...muted }}>{ts.queueSwitch.desc}</div>
        </div>
        <Switch
          checked={draft.newDeck?.isActive ?? true}
          onChange={(on) => store.setNewDeck({ isActive: on })}
          labelledBy="new-deck-queue"
        />
      </div>
    </div>
  )
}

/** The receiving deck: the question, always, with the default preselected. */
export function CardDeckChooser({ draft }: { draft: EditDraft }) {
  const { tr } = useLocale()
  const te = tr.textEdit
  const ts = tr.textFlow.save
  const store = useTextEditStore()
  const [picking, setPicking] = useState(false)
  const r = deckChoice(draft)
  // A deck chosen from «Другая колода» that the text is not linked to yet.
  const other = r.kind === 'existing' && !r.linked ? r : null

  return (
    <div data-testid="cards-deck" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Eyebrow>{te.cardsDeckQuestion}</Eyebrow>
      {r.kind === 'unchosen' && <p style={muted} data-testid="cards-deck-unchosen">{te.cardsAmongHint}</p>}
      <div
        role="radiogroup"
        aria-label={te.cardsDeckQuestion}
        style={{ borderRadius: 12, overflow: 'hidden', background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
      >
        {draft.deckIds.map((id, i) => (
          <RadioRow
            key={id}
            first={i === 0}
            on={r.kind === 'existing' && r.id === id}
            label={draft.deckTitles[id] ?? ''}
            testId="cards-deck-linked"
            onSelect={() => store.setCardDeck({ kind: 'existing_deck', id, title: draft.deckTitles[id] ?? '' })}
          />
        ))}
        {other && (
          <RadioRow first={draft.deckIds.length === 0} on label={other.title} desc={te.cardsLinkNote} testId="cards-deck-other" onSelect={() => setPicking(true)} />
        )}
        <RadioRow
          first={draft.deckIds.length === 0 && !other}
          on={r.kind === 'new'}
          label={ts.newDeck.title}
          desc={r.kind === 'new' ? null : te.cardsNewDesc}
          testId="cards-dest-new"
          onSelect={() => store.setCardDeck({ kind: 'new_deck' })}
        />
        <RadioRow
          first={false}
          on={false}
          label={te.cardsOtherDeck}
          desc={ts.existingDeck.choose}
          chevron
          testId="cards-dest-existing"
          onSelect={() => setPicking(true)}
        />
      </div>
      {r.kind === 'new' && (
        <>
          <NewDeckFields draft={draft} />
          <p style={{ ...muted, fontSize: 12, color: 'var(--text-tertiary)' }}>{te.cardsLinkNote}</p>
        </>
      )}
      <DeckPickerSheet
        open={picking}
        onClose={() => setPicking(false)}
        onPick={(deck) => { store.setCardDeck({ kind: 'existing_deck', id: deck.id, title: deck.title }); setPicking(false) }}
      />
    </div>
  )
}

/** The words marked as cards, with the receiving deck; nothing when there are none. */
export function NewCardsSection({ draft }: { draft: EditDraft }) {
  const { tr } = useLocale()
  const te = tr.textEdit
  const store = useTextEditStore()
  if (draft.cardMarks.length === 0) return null
  return (
    <section
      data-testid="edit-new-cards"
      className={CONTROL_SURFACE}
      onContextMenu={suppressTouchContextMenu}
      style={{
        display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px', borderRadius: 16,
        background: 'var(--bg-surface)', border: '1px solid var(--border)', fontFamily: 'var(--font-ui)',
      }}
    >
      <Eyebrow>{te.cardsEyebrow(draft.cardMarks.length)}</Eyebrow>
      <p style={{ ...muted, fontSize: 12 }}>{te.cardsHint}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {draft.cardMarks.map((m) => (
          <span
            key={m.start}
            data-testid="edit-card-mark"
            style={{
              display: 'inline-flex', alignItems: 'center', height: 36, paddingLeft: 12, borderRadius: 'var(--radius-pill)',
              background: 'var(--cefr-b1-bg)', fontFamily: 'var(--font-content)', fontSize: 15, color: 'var(--text-primary)',
            }}
          >
            {m.text.replace(/\s+/g, ' ')}
            <button
              type="button"
              onClick={() => store.removeCardMark(m.start)}
              aria-label={te.unmarkCard(m.text.replace(/\s+/g, ' '))}
              style={{ width: 36, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
            >
              <X size={14} aria-hidden />
            </button>
          </span>
        ))}
      </div>
      <CardDeckChooser draft={draft} />
    </section>
  )
}

const chips = (words: PlannedCardWord[], testId: string) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} data-testid={testId}>
    {words.map((w) => <WordChip key={`${w.lemma}-${w.start}`}>{w.word}</WordChip>)}
  </div>
)

/**
 * The save sheet's «Новые карточки»: where they go and what the budget allows now.
 * `check` is the server's answer for exactly this draft, null while it is asked or
 * when it failed. The save never waits on it: the PATCH charges the budget itself.
 */
export function EditCardsSummary({ draft, check, failed }: {
  draft: EditDraft
  check: TextBudgetCheck | null
  failed: boolean
}) {
  const { tr } = useLocale()
  const te = tr.textEdit
  const tb = tr.textFlow.save.budget
  const when = useRefreshWhen()
  const r = receivingDeck(draft)
  const deckTitle = r?.kind === 'existing' ? r.title : null

  const where = r?.kind === 'unchosen' ? <CardDeckChooser draft={draft} />
    : r?.kind === 'new' ? <p style={muted} data-testid="cards-deck-line">{te.cardsToNew(r.title)}</p>
    : <p style={muted} data-testid="cards-deck-line">{te.cardsTo(deckTitle ?? '')}{r?.kind === 'existing' && !r.linked ? ` ${te.cardsLinkNote}` : ''}</p>

  const view = budgetView(check, true, draft.cardMarks.length)
  const refresh = check ? when(check.budget.refresh_at) : ''

  let budget: React.ReactNode = null
  if (r?.kind === 'unchosen') budget = null
  else if (failed) budget = <p style={muted} data-testid="cards-check-failed">{te.cardsCheckFailed}</p>
  else if (!check || !view) budget = <p style={muted} data-testid="cards-checking">{te.cardsChecking}</p>
  else {
    budget = (
      <>
        {'accepted' in view && view.accepted > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <p style={{ ...muted, color: 'var(--text-primary)' }} data-testid="cards-accepted-line">{te.cardsAccepted(view.accepted)}</p>
            {chips(check.accepted, 'cards-accepted')}
          </div>
        )}
        {view.kind === 'waits' && (
          <InfoPanel testId="cards-waiting" icon={<Clock size={18} />} title={tb.panelTitle(check.budget.remaining ?? view.accepted)}>
            <p style={muted}>{tb.panelBody(view.waiting.length, refresh)}</p>
            {chips(view.waiting, 'cards-waiting-chips')}
          </InfoPanel>
        )}
        {view.kind === 'exhausted' && (
          <InfoPanel testId="cards-exhausted" icon={<Clock size={18} />} title={tb.exhaustedTitle}>
            <p style={muted}>{tb.exhaustedBody(view.waiting.length, refresh)}</p>
            {chips(view.waiting, 'cards-waiting-chips')}
          </InfoPanel>
        )}
        {check.skipped.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <p style={muted} data-testid="cards-skipped-line">{te.cardsSkipped(deckTitle ?? '')}</p>
            {chips(check.skipped, 'cards-skipped')}
          </div>
        )}
      </>
    )
  }

  return (
    <section data-testid="edit-save-cards" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
      <Eyebrow>{te.cardsEyebrow(draft.cardMarks.length)}</Eyebrow>
      {where}
      {budget}
    </section>
  )
}
